<template>
	<div v-if="isEventActive" class="shop shop--event">
		<div class="section-label">
			<span>
				{{ translate("event.event_bundles") }}
			</span>
		</div>
		<div
			class="shop-section"
			v-for="offer in eventOffers"
			:key="offer.store_id"
		>
			<PremiumShopItem
				:offer="offer"
				@openPopup="handlePopupOpen(offer.store_id)"
			/>
		</div>
		<div
			class="section-label"
			v-if="resourcesForEventiumOffers?.length || mutatedFreePack"
		>
			<span>
				{{ translate("event.event_offers") }}
			</span>
		</div>
		<div class="shop-section">
			<EventConversionOffers />
		</div>
	</div>
</template>

<script lang="ts" setup>
import EventConversionOffers from "@/components/Event/EventConversionOffers.vue";
import PremiumShopItem from "@/components/shop/cards/PremiumShopItem.vue";
import { useEventStore } from "@/event";
import useSocketService from "@/hooks/useSocketService";
import {
	OfferAnalyticsClickSource,
	OfferAnalyticsType,
} from "@/libs/SocketService";
import { OfferProvider } from "@/services/OfferProvider";
import { PaymentProvider } from "@/services/PaymentProvider";
import useShopStore from "@/store/useShopStore";
import type { ShopItem } from "@/types/shop";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";

const shopStore = useShopStore();
const { eventOffers } = storeToRefs(shopStore);

const eventStore = useEventStore();
const { isActive: isEventActive } = storeToRefs(eventStore);

const resourcesForEventiumOffers = OfferProvider.eventiumOffers;

const mutatedFreePack = OfferProvider.eventiumFreePack;

const handlePopupOpen = (storeId: string): void => {
	useSocketService().emitOfferAnalyticsSave({
		type: OfferAnalyticsType.OFFER_CLICK,
		clickSource: OfferAnalyticsClickSource.STORE ?? "UNKNOWN",
		storeId: storeId ?? "UNKNOWN",
		offerName:
			eventOffers?.value?.find(
				(offer: ShopItem): boolean => offer.store_id === storeId,
			)?.name ?? "UNKNOWN",
	});

	PaymentProvider.openPopup(storeId, {
		clickSource: OfferAnalyticsClickSource.STORE,
	});
};
</script>

<style lang="scss" scoped>
.shop {
	&--event {
		width: 100%;

		.section-label {
			@include flex-row-center;
			position: relative;
			mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);
			width: 100%;
			height: 1.875rem;

			span {
				position: relative;
				z-index: 1;
				padding-bottom: 0.25rem;
				color: #f4d278;
				font-weight: 800;
				font-size: 1.25rem;
				line-height: 1.25rem;
				text-align: center;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
			}

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				@include background(
					url($path-textures + "silk-flag-texture-bg.avif"),
					cover,
					center center,
					no-repeat
				);
				z-index: 0;
				width: 100%;
				height: 100%;
				content: "";
			}

			&::after {
				position: absolute;
				top: 0;
				left: 0;
				mix-blend-mode: hard-light;
				background: hsl(136, 79%, 15%);
				width: 100%;
				height: 100%;
				content: "";
			}
		}
		.text-button {
			:deep(.text-button) {
				gap: 0.5rem;
				padding: 0 1rem;
				width: unset;

				.dual-price {
					opacity: 0.9;
					font-size: 0.75rem;
				}
			}
		}
	}

	&-section {
		position: relative;
		@include flex-row-center;
		flex-wrap: wrap;
		gap: 1rem;
		width: 100%;
	}
}
</style>
